import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { CloseButton } from '@/components/ds';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useClientGroups, useCreateListing } from '@/hooks/useClientGroups';
import { useTranslationPrefix } from '@/translations/i18n';
import { Deal } from '@/types/clientGroups';

export interface DealPickerModalProps {
  open: boolean;
  propertyAddress: string;
  propertyId: string;
  groupId?: string;
  buyerEmail?: string;
  onClose: () => void;
  onDone: () => void;
}

const font = "'Manrope', sans-serif";

export const DealPickerModal = ({
  open,
  propertyAddress,
  propertyId,
  buyerEmail,
  onClose,
  onDone,
}: DealPickerModalProps) => {
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const { groups } = useClientGroups();
  const createListing = useCreateListing();
  const [selectedDealId, setSelectedDealId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const lowerBuyer = buyerEmail?.toLowerCase();
    return (groups ?? [])
      .filter(
        (g) => !lowerBuyer || (g.members ?? []).some((m) => m.email?.toLowerCase() === lowerBuyer),
      )
      .map((g) => {
        const activeDeals = g.deals.filter((d: Deal) => d.status !== 'Closed');
        if (!q) return { group: g, deals: activeDeals };
        const matchGroup = g.holdingName.toLowerCase().includes(q);
        const matchDeals = activeDeals.filter((d: Deal) => d.name.toLowerCase().includes(q));
        if (matchGroup) return { group: g, deals: activeDeals };
        if (matchDeals.length > 0) return { group: g, deals: matchDeals };
        return null;
      })
      .filter((x): x is NonNullable<typeof x> => x !== null && x.deals.length > 0);
  }, [groups, search, buyerEmail]);

  const handleSave = async () => {
    if (!selectedDealId || isSaving) return;
    setIsSaving(true);
    try {
      await createListing.mutateAsync({
        dealId: selectedDealId,
        data: { address: propertyAddress, status: 'Touring', propertyId },
      });
      showAlert(t('deal.property_added'), 'success');
      onDone();
    } catch {
      showAlert(t('deal.property_add_failed'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) return;
    setSelectedDealId('');
    setSearch('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth={false}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'var(--tx-glass-panel)',
            backgroundImage: 'none',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: 0,
            borderRadius: '25px',
            width: '100%',
            maxWidth: '480px',
            m: 3,
            boxShadow: 'none',
          },
        },
      }}>
      <CloseButton
        onClose={() => {
          if (!isSaving) handleCancel();
        }}
        label="Close"
        sx={{ position: 'absolute', top: 18, right: 18 }}
      />

      <DialogContent sx={{ p: '25px', pt: '62px' }}>
        <Typography
          sx={{ fontSize: 14, color: 'var(--tx-muted)', lineHeight: 1.5, mb: '16px', fontFamily: font }}>
          Assign{' '}
          <Box component="span" sx={{ color: 'var(--tx-text)', fontWeight: 600 }}>
            {propertyAddress.split(',')[0]}
          </Box>{' '}
          to a deal.
        </Typography>

        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search clients or deals…"
          style={{
            width: '100%',
            background: 'var(--tx-fill)',
            border: 0,
            borderRadius: 8,
            padding: '8px 12px',
            color: 'var(--tx-text)',
            fontSize: 13,
            fontFamily: font,
            outline: 'none',
            boxSizing: 'border-box',
            marginBottom: 12,
          }}
        />

        <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
          {filtered.length === 0 && (
            <Typography sx={{ fontFamily: font, fontSize: 12, color: '#737373', py: '10px' }}>
              {search.trim() ? 'No matches found' : 'No active deals found'}
            </Typography>
          )}
          {filtered.map(({ group: g, deals }) => (
            <Box key={g.id} sx={{ mb: '8px' }}>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--tx-muted)',
                  fontFamily: font,
                  mb: '4px',
                }}>
                {g.holdingName}
              </Typography>
              <Box sx={{ ml: '12px', borderLeft: '1px solid rgba(255,255,255,0.1)', pl: '12px' }}>
                {deals.map((d: Deal) => {
                  const isSelected = d.id === selectedDealId;
                  return (
                    <Box
                      key={d.id}
                      onClick={() => !isSaving && setSelectedDealId(d.id)}
                      sx={{
                        padding: '7px 10px',
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: font,
                        cursor: isSaving ? 'default' : 'pointer',
                        borderRadius: '8px',
                        color: isSelected ? 'var(--tx-text)' : 'var(--tx-text)',
                        background: isSelected ? 'var(--tx-fill-2)' : 'transparent',
                        border: 0,
                        transition: 'background 0.1s',
                        '&:hover': !isSelected ? { background: 'rgba(187,183,175,0.12)' } : {},
                      }}>
                      {d.name}
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: isSelected ? '#444' : 'var(--tx-faint)',
                          fontFamily: font,
                          mt: '1px',
                        }}>
                        {d.periodType} · Started {d.engagementStart}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: '25px', py: '20px', gap: 1.5 }}>
        <Button
          onClick={handleCancel}
          disabled={isSaving}
          variant="text"
          sx={{
            color: 'var(--tx-text)',
            fontSize: 14,
            fontWeight: 600,
            height: 44,
            borderRadius: '12px',
          }}>
          Cancel
        </Button>
        {filtered.length > 0 && (
          <Button
            onClick={handleSave}
            disabled={!selectedDealId || isSaving}
            variant={selectedDealId ? 'contained' : 'outlined'}
            startIcon={
              isSaving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : undefined
            }
            sx={{
              borderRadius: '12px',
              fontSize: 14,
              fontWeight: 600,
              ...(selectedDealId
                ? {
                    backgroundColor: 'var(--tx-lime)',
                    color: '#262626',
                    border: 0,
                    '&:hover': { backgroundColor: '#B0EF00' },
                  }
                : {
                    border: 0,
                    backgroundColor: 'var(--tx-fill)',
                    color: 'var(--tx-faint)',
                    '&:hover': { backgroundColor: 'var(--tx-fill-2)' },
                  }),
              '&.Mui-disabled': {
                border: 0,
                color: 'var(--tx-faint)',
                backgroundColor: 'var(--tx-fill)',
              },
            }}>
            {isSaving ? 'Saving…' : 'Assign'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
